/**
 * AuthGate — the whole-studio login wall, as a tiny eager SHELL (#764 gate-8
 * split over the #462/#528/#572 deferral chain).
 *
 * Auth is OFF by default (`isAuthEnabled()`, gated on the runtime config's
 * `VITE_AUTH_ENABLED`, #572), and the auth-off render is EXACTLY "children under
 * a null {@link SessionProvider}" — so that is ALL this eager module does. The
 * full gate body (the `ensureAuth` boot flow, the #455 sign-in-failure recovery
 * screen, the #S-16 expiry `ReloginPrompt` subscription, the #264 identity/
 * Sign-out control, and the SDK `Button` primitive they pull) lives in the LAZY
 * {@link AuthGateEnabled} chunk, reached only when auth is ON.
 *
 * Behaviour-identical by construction: the auth-on path ALREADY rendered the
 * `auth-loading` "Signing in…" wall while `ensureAuth` ran (an async Auth0
 * round-trip); the shell's Suspense fallback renders the SAME wall while the
 * chunk loads, then the full gate keeps rendering it until `ensureAuth`
 * resolves — one continuous loading state, no flash. The runtime config is
 * loaded BEFORE first render (`main.tsx` awaits `loadRuntimeConfig()`), so the
 * `isAuthEnabled()` read here sees the served value, same as before.
 *
 * @module @ubiclouds/v5-studio/auth/AuthGate
 */

import { lazy, Suspense, type CSSProperties, type ReactElement, type ReactNode } from 'react';

// The full auth-ON gate — a lazy chunk (#764). NEVER statically imported (the
// split-point test pins this): the default no-auth boot must not pay for it.
const AuthGateEnabled = lazy(() => import('./AuthGateEnabled.js'));

// Mirrors the full gate's `auth-loading` wall so the chunk-load window and the
// `ensureAuth` window render one continuous "Signing in…" state.
const centre: CSSProperties = {
  display: 'grid',
  placeContent: 'center',
  height: '100%',
  textAlign: 'center',
  gap: '0.5rem',
  color: 'var(--os-text-muted)',
};

export function AuthGate({
  enabled = false,
  children,
}: {
  /**
   * Whether the Auth0 login wall is enforced. #1272 — supplied by the BOOT
   * (`main.tsx`) from the same `isAuthEnabled()` leaf as before, read once after
   * `loadRuntimeConfig()` resolves, instead of being imported here. That import
   * was the ONLY eager reader of `auth/auth-enabled.ts` and, through it, of
   * `config/runtime-config.ts` — two modules on the first-paint closure for a
   * value the boot already had in hand. Behaviour is unchanged: the flag was
   * ALREADY effectively boot-constant (the docblock above records that the
   * config is loaded before first render), so reading it once at boot resolves
   * exactly the value every render read.
   *
   * DEFAULTS TO `false` — the off-by-default contract (#184) is now expressed as
   * the default of this prop rather than as the default of the config read, and
   * `AuthGate.disabled.test.tsx` still proves it from a bare `<AuthGate>`.
   */
  readonly enabled?: boolean;
  children: ReactNode;
}): ReactElement {
  if (!enabled) {
    // Auth OFF (the default deploy / dev / tests): children render DIRECTLY —
    // no login wall, no "Signing in…" phase, no Auth0 module load.
    //
    // #1272 — and no `SessionProvider` either. It published `value={null}`,
    // which is EXACTLY `SessionContext`'s own default, so every `useSession()`
    // consumer sees the identical `null` with or without it; its only other
    // effect is publishing the identity/logout module refs, which for a `null`
    // session writes their already-`null` initial value. Dropping it on the
    // auth-OFF path evicts `session-context.tsx` from the eager entry (the
    // module rides the lazy chunks that actually read it — `/ide`'s UserMenu
    // claim and the agent identity PERCEIVE). `AuthGateEnabled` (lazy) still
    // renders it on the auth-ON path, where the value is a real session.
    return <>{children}</>;
  }
  return (
    <Suspense
      fallback={
        <main style={centre} data-testid="auth-loading">
          Signing in…
        </main>
      }
    >
      <AuthGateEnabled>{children}</AuthGateEnabled>
    </Suspense>
  );
}
